// Third-parties.
import { useContext, useEffect, useState } from "react";

// Context
import { InstanceContext } from "../../../context/InstanceContext";

// Components
import EventsTable from "../../../component/instance/table/EventsTable";

// Types
import EventsDataTable from "../../../types/instance/tables/EventsDataTable";
import { InstanceOptions } from "./types";
import Helper from "../../../helpers/Helper";
import { formatPeriodHeadingFromDate, getTimeZone } from "../../../helpers/utils";
import CombinedChanges from "../../../types/instance/CombinedChanges";

// Helpers
import ConditionalRender from "../../../helpers/ConditionalRender";

// Styles
import "../../overflow-tabbed-menu/style.scss";

const ActivityComparisonEvents = (props: {
    combinedChanges: CombinedChanges[][],
    instanceOptions: InstanceOptions[],
    loading: boolean,
    generateId: number
}) => {
    const { combinedChanges, loading } = props
    const [eventsDataTable, setEventsDataTable] = useState<EventsDataTable[]>([])
    const [activeTab, setActiveTab] = useState(0)
    const [options, setOptions] = useState<InstanceOptions[]>(props.instanceOptions)

    const { instances } = useContext(InstanceContext);

    useEffect(() => {
        setOptions(props.instanceOptions)
    }, [props.generateId])

    const timezone = getTimeZone();
    const getTabDateFormat = (instance: any) => {
        return formatPeriodHeadingFromDate(instance.periodStart, instance.periodEnd, timezone);
    };

    const getInstanceName = (id: any) => {
        const instance = instances.find(instance => instance.id === parseInt(id))
        return instance ? instance.name : ''
    }

    useEffect(() => {
        let eventsData: EventsDataTable[] = [];
        if (combinedChanges.length) {

            const combinedEvents = combinedChanges[activeTab].map((event: any, index: number) => {
                return {
                    ...event,
                    id: index,
                    instanceId: event.datasourceid,
                    timeslice: event.timeslice,
                }
            })
            combinedEvents.forEach((event: any) => {
                let changeType: string,
                        htmlIconCode: string = '',
                        detailsUrl: string = '',
                        color: string = '',
                        title: string = '',
                        details: string = event.name;

                const dataSourceType = event.object === 'datasource' ? 'instance' : event.object
                changeType = `${dataSourceType} ${event.type}`

                if (event.event === 'custom') {
                    changeType = event.type
                    details = event.description
                }
                eventsData.push({
                    id: event.id,
                    instanceId: event.instanceId,
                    event: event.event,
                    changeType,
                    changeFrom: event.oldvalue,
                    changeTo: event.newvalue,
                    htmlIconCode,
                    instanceName: getInstanceName(event.instanceId),
                    detailsUrl,
                    color,
                    title,
                    details,
                    isHost: !!event.isHost,
                    timeslice: event.timeslice,
                    startedEnded: event.startedended,
                    statistic: event.statistic,
                    threshold: event.threshold,
                    decreasing: event.decreasing,
                    units: event.units,
                    timesliceEnd: ((event.isPlotband === true) ? event.endDateTime : undefined)
                });
            });
            setEventsDataTable(eventsData)
        }

    }, [combinedChanges, activeTab])

    return (
            <div>

                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-exchange fa-fw" aria-hidden="true"/>
                        Changes, events and alerts history

                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseTable" aria-expanded="false"
                           aria-controls="collapseTable"/>
                    </div>

                    <div id="collapseTable" className="card-body collapse show">
                        <div id="menu-inner" className="nav nav-tabs" role="tablist">
                            <a 
                                onClick={() => setActiveTab(0)}
                                id="previous-tab"
                                role="tab"
                                aria-controls="changes-previous"
                                aria-selected={activeTab === 0}
                                className={activeTab === 0 ? "active" : ""}
                                href="#changes-previous"
                            >
                                {getTabDateFormat(options[0])}
                                <span className="badge bg-info"
                                      data-tooltip-id="global-tooltip" data-tooltip-content="Total period A change count">{Helper.getFormattedNumber(combinedChanges[0].length)}</span>
                            </a>
                            <a 
                                onClick={() => setActiveTab(1)}
                                id="current-tab"
                                role="tab"
                                aria-controls="changes-current"
                                aria-selected={activeTab === 1}
                                className={activeTab === 1 ? "active" : ""}
                                href="#changes-current"
                            >
                                {getTabDateFormat(options[1])}
                                <span className="badge bg-info"
                                      data-tooltip-id="global-tooltip" data-tooltip-content="Total period B change count">{Helper.getFormattedNumber(combinedChanges[1].length)}</span>
                            </a>
                            <ConditionalRender if={Helper.showInterimChanges(options)}>
                                <a 
                                    onClick={() => setActiveTab(2)}
                                    id="interim-tab"
                                    role="tab"
                                    aria-controls="changes-interim"
                                    aria-selected={activeTab === 2}
                                    className={activeTab === 2 ? "active" : ""}
                                    href="#changes-interim"
                                >
                                    Interim Changes ({getTabDateFormat({
                                        periodStart: options[0].periodEnd,
                                        periodEnd: options[1].periodStart
                                    })})
                                    <span className="badge bg-info"
                                          data-tooltip-id="global-tooltip" data-tooltip-content="Total interim change count">{Helper.getFormattedNumber(combinedChanges[2].length)}</span>
                                </a>
                            </ConditionalRender>
                        </div>
                        <ConditionalRender if={loading}>
                            <div className="w-100 text-center text-muted my-3">
                                <div className="loader spinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={!loading}>
                            <EventsTable hideRegister={true} loading={false} eventsDataTable={eventsDataTable}/>
                        </ConditionalRender>
                    </div>
                </div>
            </div>

    )
}

export default ActivityComparisonEvents;